/**
 * Text customisation — gate the product form on the required free-text fields,
 * and compose the birth-statistics values.
 *
 * The product form (`buy-buttons.liquid`) is rendered with `novalidate`, so the
 * `required` attributes on the fields do not block a submit on their own. This
 * listens in the capture phase on `document` — which runs before
 * `product-form.js`'s own submit handler on the form — and stops the event
 * there while any field is still blank.
 *
 * The simple fields are attached to the form with the `form` attribute rather
 * than by being nested in it, so the submitted values are read from
 * `form.elements`. The time / weight / height fields are not named at all: their
 * visible controls are read directly and written onto a hidden, form-attached
 * input as one fixed-format value ("4:35 AM", "3.20 kg", "50.0 cm").
 */
if (!customElements.get('text-customisation')) {
  customElements.define(
    'text-customisation',
    class extends HTMLElement {
      connectedCallback() {
        this.formId = this.dataset.formId;
        this.messageEmpty =
          this.dataset.messageEmpty || 'Please fill in the fields below before adding to cart.';
        this.errorEl = this.querySelector('[data-error]');
        this.fields = [...this.querySelectorAll('[data-required]')];

        this.form = this.formId ? document.getElementById(this.formId) : this.closest('form');
        if (!this.form || !this.fields.length) return;

        this.onSubmit = this.onSubmit.bind(this);
        this.onEdit = this.onEdit.bind(this);
        document.addEventListener('submit', this.onSubmit, true);
        this.addEventListener('input', this.onEdit);
        this.addEventListener('change', this.onEdit);

        // restore the composed values on a back / forward navigation
        this.compose();
      }

      disconnectedCallback() {
        document.removeEventListener('submit', this.onSubmit, true);
        this.removeEventListener('input', this.onEdit);
        this.removeEventListener('change', this.onEdit);
      }

      /**
       * Write each composed value onto its hidden input, or clear it while the
       * customer is still filling the group in.
       */
      compose() {
        this.querySelectorAll('[data-compose]').forEach((group) => {
          const output = group.querySelector('[data-output]');
          if (!output) return;

          const parts = [...group.querySelectorAll('[data-source]')].map((el) =>
            (el.value || '').trim()
          );
          if (!parts.length || parts.some((part) => !part)) {
            output.value = '';
            return;
          }

          const unit = group.dataset.unit;
          output.value = group.hasAttribute('data-time')
            ? `${parts[0]}:${parts[1]} ${parts[2]}`
            : `${parts[0]}${unit ? ` ${unit}` : ''}`;
        });
      }

      /** A named field lives in `form.elements`; an unnamed one is read directly. */
      valueOf(field) {
        const named = field.name ? this.form.elements.namedItem(field.name) : null;
        const raw = named ? named.value : field.value;
        return (raw || '').trim();
      }

      blankFields() {
        return this.fields.filter((field) => !this.valueOf(field));
      }

      onEdit() {
        this.compose();
        this.clear();
      }

      onSubmit(event) {
        if (event.target !== this.form) return;

        this.compose();

        const empty = this.blankFields();
        if (!empty.length) return;

        event.preventDefault();
        event.stopPropagation();

        this.show(this.messageEmpty);

        const first = empty[0];
        if (first.reportValidity) first.reportValidity();
        first.focus({ preventScroll: true });
        this.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }

      show(message) {
        if (!this.errorEl) return;
        this.errorEl.textContent = message;
        this.errorEl.hidden = false;
      }

      /** Keep the warning up until the last blank field is filled in. */
      clear() {
        if (!this.errorEl || this.errorEl.hidden) return;
        if (!this.blankFields().length) this.errorEl.hidden = true;
      }
    }
  );
}
